import type { CSSProperties } from 'react';
import { formatMoney } from '../_shared/formatMoney';

/** The visible card surface every marketplace card/panel sits on — a
 *  bordered, rounded white card (akademi-sem's `bg-bgPrimary rounded-lg
 *  shadow-sm`). Spread into an `@akademi/ui` `<Card style>` in place of the
 *  neutralising `cardReset`, which leaves the card transparent + borderless. */
export const mpSurface: CSSProperties = {
  display: 'block',
  flexDirection: 'row',
  gap: 0,
  padding: 0,
  background: 'var(--color-surface-card, #fff)',
  border: '1px solid var(--color-border-default, rgba(0,0,0,0.14))',
  borderRadius: 'var(--radius-lg, 12px)',
  color: 'inherit',
};

/**
 * Authored price for a marketplace block — an admin types amount + currency
 * into the page designer, unlike `commerce/`'s `MoneyValue` which arrives
 * from a live storefront API and also carries `taxInclusive`. Kept as its
 * own (smaller) shape so the JSON schema for every catalog/course item
 * doesn't force an editor to fill in a tax flag that has no live backend
 * behind it here.
 */
export interface MarketPrice {
  amount: string;
  currency: string;
}

/** `formatMoney`, reusing its decimal-safe `Intl.NumberFormat` formatting
 *  without requiring the full `MoneyValue` shape (see {@link MarketPrice}). */
export function formatMarketPrice(
  price: MarketPrice | undefined,
  locale: string,
): string | undefined {
  if (!price?.amount || !price.currency) return undefined;
  return formatMoney({ ...price, taxInclusive: true }, locale);
}

/** Order statuses that mean "already paid" — no more gateway buttons, show
 *  a "start learning" action instead. Used by `MarketplaceCheckoutCta`'s
 *  `mode="order"`. */
export const ORDER_APPROVED_STATUSES = new Set(['APPROVED', 'COMPLETED', 'PAID']);

/** i18n key per payment gateway id, for the "pay with…" buttons on a
 *  specific order. Used by `MarketplaceCheckoutCta`'s `mode="order"`. */
export const GATEWAY_LABEL_KEYS: Record<string, string> = {
  bank_transfer: 'marketplace.paymentMethod.bankTransfer',
  stripe: 'marketplace.paymentMethod.creditCard',
  paytr: 'marketplace.paymentMethod.creditCard',
  ipaytr: 'marketplace.paymentMethod.creditCard',
};

/** Localized label for a `MarketplaceOrderStatus` — falls back to the raw
 *  backend enum untranslated (better than a blank badge) when it's a value
 *  we don't have a translation for. `t` is passed in rather than called
 *  here so this stays a plain function callers can use outside a
 *  component's own `useTranslation()` scope-free. */
export function formatOrderStatus(
  status: string,
  t: (key: string, opts: { defaultValue: string }) => string,
): string {
  const key = status.toLowerCase();
  const known = ['pending', 'approved', 'completed', 'cancelled', 'paid'];
  if (!known.includes(key)) return status;
  return t(`marketplace.orderStatus.${key}`, { defaultValue: status });
}

/**
 * Whether a live cart's `total` is exactly zero — fully covered by a
 * coupon, or an all-free selection. No total yet (still loading) counts as
 * NOT free, so a block doesn't flicker hidden before the first fetch
 * resolves. Shared by every step that only matters once money is actually
 * changing hands ({@link MarketplaceCheckoutCta}, `MarketplaceCouponCode`,
 * `MarketplacePaymentMethod`, `MarketplaceAddressSelect`) — each exposes a
 * `showWhenFree` prop (default `false`) that hides the block outright once
 * this is true, unless the author opts back in.
 */
export function isCartTotalZero(cart: { total?: { amount: string } } | null | undefined): boolean {
  const amount = cart?.total?.amount;
  return amount != null && parseFloat(amount) === 0;
}

/**
 * Whether a live cart has no line items at all — nothing to discount, pay
 * for, ship, or check out. `cart` still `null` (nothing fetched yet) counts
 * as NOT empty, so a block doesn't flicker hidden before the first fetch
 * resolves — mirrors {@link isCartTotalZero}'s posture. Unlike that one,
 * this hides unconditionally (no author opt-back-in): an empty cart isn't
 * a checkout state any of these blocks make sense in.
 */
export function isCartEmpty(cart: { items: unknown[] } | null | undefined): boolean {
  return cart != null && cart.items.length === 0;
}

/**
 * The current page's `/marketplace` root, derived from `window.location`
 * instead of hardcoded — basePath-agnostic (works whether the host mounts
 * under `/portal` or not). For hard navigations (`window.location.href =`)
 * off a marketplace page: a relative `../x` string is the wrong tool here
 * (it strips whatever segment follows `/marketplace/`, not `/marketplace`
 * itself, landing one level too high — the bug behind `MarketplaceCheckoutCta`'s
 * original redirect). `<PortalLink>`/`next/link` don't need this — they
 * resolve basePath on their own.
 */
export function getMarketplaceRoot(): string {
  return window.location.pathname.replace(/\/marketplace\/.*$/, '/marketplace');
}
